import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import type { APIData, DSSetup, PopupData, RequestError, Service } from 'webapp-types';
import {
	BatchSelectActionType,
	BillingFeature,
	FakeModelIdPrefix,
	ModelName,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { compact, uniqueId } from 'webapp/utils/common';

import CommonPopupApiResponseComponent from 'webapp/components/common/popups/popup-api-response';
import CustomMetricsEditPopupComponent from 'webapp/components/custom-metrics/edit/popup';
import CustomQueryPurgePopupComponent from 'webapp/components/custom-query/popup-purge';

import CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type { PreviewErrorData } from 'webapp/models-custom/custom-metric-setup-form/base';
import type CustomMetricSetupFormBase from 'webapp/models-custom/custom-metric-setup-form/base';
import CustomMetricSetupFormDataset from 'webapp/models-custom/custom-metric-setup-form/dataset';
import CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import CustomQuery from 'webapp/models-custom/custom-query';
import type CustomQueryRecord from 'webapp/models/custom-query';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { BatchSelectActionOptions } from 'webapp/services/model-manager/for-store';
import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

export type TrackingOriginForNewQuery = 'listing';

export default class ModelManagerCustomQueriesService extends ModelManagerForStoreService<
	CustomQueryRecord,
	CustomQuery
> {
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.Account;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	get customQueryModels(): CustomQuery[] {
		return this.createModelsFromRecordsArray(this.savedRecords);
	}

	get savedRecordsNotFromPublicTemplates(): CustomQueryRecord[] {
		return this.savedRecords.filter((item) => !item.isFromPublicTemplate);
	}

	openEditPopup(
		model: CustomQuery,
		origin: TrackingOriginForMetricEdit | 'dataset_created_succesfully',
		openMetricDefinitionPopupOnInit?: boolean,
		afterSave?: PopupData.CustomMetric.Edit['afterSave'],
		onDelete?: PopupData.CustomMetric.Edit['onDelete'],
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (model.sourceObj?.isSourceDisabledForUsage) {
			this.infolayerService.warning(model.sourceObj.sourceDisabledForUsageInfo);
			return;
		}

		if (this.billingService.checkIfCan(BillingFeature.QB, true)) {
			const popup = this.mmMetricsService.createCustomMetricEditPopup();

			const modelCopy = model.isNew ? model : this.createModel(model.model);

			popup.open({
				model: modelCopy,
				origin,
				openMetricDefinitionPopupOnInit,
				afterSave,
				onDelete,
			});

			return popup;
		}
	}

	openEditPopupWithTransition(model: CustomQuery): PublicTransition {
		// The id, not the record: given a record, the route skips its model hook, which looks up the custom query.
		return this.navigationService.transitionToRoute('metrics.edit.built', model.idForSure);
	}

	openCreateNewPopup(
		origin: TrackingOriginForMetricEdit | 'dataset_created_succesfully',
		afterSave?: PopupData.CustomMetric.Edit['afterSave'],
		sourceId?: number,
	) {
		if (this.billingService.checkIfCan(BillingFeature.CUSTOM_METRICS, true)) {
			const model = this.createNewModel({ sourceId, sourceToId: sourceId });
			return this.openEditPopup(model, origin, undefined, afterSave);
		}
	}

	openViewPopup(model: CustomQuery, origin: TrackingOriginForMetricEdit) {
		const popup = this.mmMetricsService.createCustomMetricEditPopup();

		popup.open({
			model: this.createModel(model.model),
			origin,
			viewOnly: true,
		});

		return popup;
	}

	createBlockFromQuery(model: CustomQuery): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const metricSetup = model.metricSetup;

		if (!metricSetup.key) {
			return;
		}

		this.mmBlocksService.openCreateFromMetricSetupPopup({
			metricSetup,
			trackOrigin: 'allcustomqueries',
			createdNow: false,
		});
	}

	cloneQuery(
		model: CustomQuery,
		origin: TrackingOriginForMetricEdit,
		afterSave?: PopupData.CustomMetric.Edit['afterSave'],
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const data = model.getDataForClone();

		const cloned = this.createNewModel(data);
		return this.openEditPopup(cloned, origin, false, afterSave);
	}

	saveCustomQuery(
		model: CustomQuery,
		form: CustomMetricSetupFormBase,
		origin: string,
	): Promise<void> {
		const isNew = model.isNew;

		form.checkIfTransformsValid();

		model.settings = form.prepareSettings();
		model.transformations = form.transformsData || {};

		if (form instanceof CustomMetricSetupFormSheet) {
			model.defaultTitle = form.getDefaultTitleInfo();
		} else {
			model.defaultTitle = null;
		}

		const layer = this.infolayerService.loading('Saving your built metric...');
		return model.save().then(
			() => {
				layer.ok('Built metric saved!');

				form.previewTakingTooLongInfolayer?.close();

				const sourceType = model.sourceObj?.sourceType;

				if (sourceType) {
					const isDataset = model.sourceObj?.isTypeOfDataset;

					const eventName = isNew
						? TrackingEvent.QUERY_CREATED
						: TrackingEvent.QUERY_EDITED;
					const eventData: APIData.Tracking.Data = {
						origin,
						datasource: sourceType.key,
						type: isDataset
							? model.sourceObj?.selectedSourceOption?.slice(0, -1)
							: model.trackingWizardType,
					};

					if (sourceType.isCustomRESTApi) {
						eventData.api_url = (form as CustomMetricSetupFormApiMetricBuilder).baseUrl;
					}

					this.analyticsService.event(eventName, eventData);
				}

				const metric = model.metric;
				if (metric) {
					// reset limitations from the query metric
					metric.cleanupLimitationsCache();

					// reset dimension values in case we renamed some...
					this.mmMetricsService.cleanupDimensionValuesCacheForMetricData(
						metric.metricData!,
					);
				}
			},
			(errorData: RequestError) => {
				// cleanup after showing because some errors e.g. rate limit are 422 but should not block the form from saving again even without model getting dirty
				model.cleanupErrorsIfOnlyGeneral();

				layer.close();

				if (errorData.isBlockedOnDemo) {
					this.accountService.handleDemo();
				} else {
					this.showErrorInPopup(errorData, 'Error saving built metric');
				}

				throw errorData;
			},
		);
	}

	checkMigrateData(model: CustomQuery, form: CustomMetricSetupFormBase): Promise<void> {
		const isEdit = !model.isNew,
			sourceType = model.sourceObj?.sourceType,
			alwaysAsk = sourceType?.isSheets || sourceType?.isSqlishConnector;

		return new Promise((resolve, reject) => {
			const updateMigrateData = (value: boolean) => {
				model.migrateData = value;
				resolve();
			};

			if (isEdit && alwaysAsk) {
				if (form.checkAreItemsWithDefinesRawIdChanged()) {
					const popup = this.popupService.createModal(CustomQueryPurgePopupComponent, {
						popupClass: 'popup-customquery-purge title-bigger',
					});
					popup.open({
						onSkip: reject,
						onSave: (purge: boolean) => {
							updateMigrateData(!purge);
						},
					});
				} else {
					updateMigrateData(true);
				}
			} else {
				updateMigrateData(true);
			}
		});
	}

	showErrorInPopup(data: PreviewErrorData, fallbackText = 'Built metric error'): void {
		let errorText: string | JQuery<HTMLElement> = '';

		const isHtml = !!data.html;

		if (isHtml) {
			errorText = data.html || '';
		} else {
			try {
				errorText = (data.errors || [])
					.map((error) => {
						return error.message;
					})
					.join('. ');
			} catch (_e) {
				// skip
			}
		}

		if (!errorText) {
			errorText = fallbackText;
		}

		const popup = this.popupService.createModal(CommonPopupApiResponseComponent, {
			popupClass: 'popup-type-api-response',
		});

		popup.open({
			title: 'Metric builder error',
			data: errorText,
			isHtml,
			wrapText: true,
		});
	}

	deleteQuery(model: CustomQuery): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const infolayer = this.infolayerService.loading('Deleting custom metric...');
			model
				.delete()
				.then(
					() => {
						infolayer.ok('Custom metric has been deleted.');
					},
					(errorData) => {
						infolayer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete this custom metric "${model.queryName}"?`,
			text: '',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	repositionEditPopup() {
		const popup = this.popupService.findByComponent(CustomMetricsEditPopupComponent);
		popup?.reposition();
	}

	// ------ For fake blocks (preview) ------

	generateTemporaryFakeId() {
		return -1 * Number(`${FakeModelIdPrefix.METRIC_BUILDER}${uniqueId()}`);
	}

	isTemporaryFakeId(id: string | null = '') {
		const fakeModelPrefix = FakeModelIdPrefix.METRIC_BUILDER;
		const length = fakeModelPrefix.length;
		return !!id && id.substring(1, 1 + length) === fakeModelPrefix;
	}

	// ------ Requests ------

	_customQueriesRequested = false;
	_customQueriesRequestPromise: Promise<void> | null = null;
	requestCustomQueries(): Promise<void> {
		const variablePrefix = 'customQueries';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(this.modelName);
			},
		);
	}

	// ------ Custom query setup form models ------

	createSetupFormForCustomQuery(
		model: CustomQuery,
	):
		| CustomMetricSetupFormSheet
		| CustomMetricSetupFormPrebuilt
		| CustomMetricSetupFormApiMetricBuilder
		| CustomMetricSetupFormDataset {
		let formObj;

		if (model.isDataset || model.isMergedDataset) {
			formObj = new CustomMetricSetupFormDataset(model);
		} else if (model.isSheets) {
			formObj = new CustomMetricSetupFormSheet(model);
		} else if (model.isApiMetricBuilder) {
			formObj = new CustomMetricSetupFormApiMetricBuilder(model);
		} else {
			formObj = new CustomMetricSetupFormPrebuilt(model);
		}

		return formObj;
	}

	// ------ Batch actions ------

	batchPurgeDataAction(items: CustomQuery[], options?: BatchSelectActionOptions): Promise<void> {
		// the metric setup identifies the metric on its source; the backend purges every
		// data configuration of it, so no dimensions or filters are sent
		const metricSetups: DSSetup.MetricSetupData[] = compact(
			items.map((item) => item.metric?.metricData),
		);
		return this.batchAction(
			BatchSelectActionType.PURGE_DATA_FOR_METRIC_SETUPS,
			undefined,
			metricSetups,
			options,
		);
	}

	// ------ For-store ------

	modelName = ModelName.CUSTOM_QUERY;
	override modelTitle = 'Built metric';

	createModel(model: CustomQueryRecord): CustomQuery {
		return new CustomQuery({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/custom-queries': ModelManagerCustomQueriesService;
	}
}
